// src/lib/pickLang.js — Phase A.3 (PR #192, ADR-112)
//
// Helper i18n livret : sélectionne la traduction appropriée d'un champ JSONB
// (ex: { fr: "Bienvenue", en: "Welcome" }).
//
// Frontend-only — pas de mirror CJS car les endpoints API exposent les JSONB
// bruts et c'est le client qui choisit la langue à l'affichage.
//
// M-2 (pessimist-pr192-pre-code) : utiliser `lang in jsonb` pour détecter la
// présence d'une clé (string vide '' considérée comme PRÉSENTE mais vide).
// Évite l'ambiguïté `isFallback:true` quand la traduction existe mais est vide.

/**
 * Sélectionne la traduction d'un champ JSONB i18n.
 *
 * Règles :
 *   1. Si `lang` présente dans jsonb (même valeur vide) → retourne sa valeur,
 *      isFallback=false
 *   2. Sinon si `fallback` présente dans jsonb → retourne fallback, isFallback=true
 *   3. Sinon retourne value='', isFallback=false
 *
 * Valeurs non-string ignorées (number, boolean, object) → traitées comme absentes.
 *
 * @param {unknown} jsonb - Objet JSONB type { fr: "...", en: "..." } ou null
 * @param {string} lang - Langue demandée ('fr', 'en', etc.)
 * @param {string} [fallback='fr'] - Langue de repli
 * @returns {{value:string, isFallback:boolean}}
 */
export function pickLang(jsonb, lang, fallback = "fr") {
  if (!jsonb || typeof jsonb !== "object" || Array.isArray(jsonb)) {
    return { value: "", isFallback: false };
  }
  // M-2 : `lang in jsonb` détecte la présence de la clé (même valeur '')
  if (lang in jsonb) {
    const v = jsonb[lang];
    if (typeof v === "string") return { value: v, isFallback: false };
    // Clé présente mais valeur non-string (number, etc.) → traitée comme absente
  }
  if (lang !== fallback && fallback in jsonb) {
    const v = jsonb[fallback];
    if (typeof v === "string") return { value: v, isFallback: true };
  }
  return { value: "", isFallback: false };
}

/**
 * Shortcut pour rendu direct sans badge fallback (ignore isFallback).
 *
 * @param {unknown} jsonb
 * @param {string} lang
 * @param {string} [fallback='fr']
 * @returns {string}
 */
export function pickLangText(jsonb, lang, fallback = "fr") {
  return pickLang(jsonb, lang, fallback).value;
}

// ─────────────────────────────────────────────────────────────────────
// Résolveur de langue à repli EN CHAÎNE — services voyageur multilingues
// (gate d'activation ES). Distinct de pickLang() : ici une valeur VIDE est
// traitée comme « non traduite » et déclenche le repli — un voyageur ES sans
// libellé ES verra l'anglais plutôt qu'un blanc (choix UX services, pas la
// sémantique M-2 « vide présent = intentionnel » du livret).
// ─────────────────────────────────────────────────────────────────────

/**
 * Chaîne de repli déterministe par langue demandée : es → en → fr, en → fr,
 * fr → en. La langue demandée est toujours essayée en premier.
 */
export const SERVICE_FALLBACK_CHAIN = {
  fr: ["fr", "en"],
  en: ["en", "fr"],
  es: ["es", "en", "fr"],
};

/**
 * Sélectionne la traduction d'un champ JSONB i18n via une chaîne de repli
 * déterministe. Ignore les valeurs vides/non-string (repli sur la suivante).
 *
 * @param {unknown} jsonb - Objet type { fr, en, es } ou null
 * @param {string} lang - Langue demandée ('fr' | 'en' | 'es')
 * @param {string[]} [chain] - Ordre de repli explicite (défaut : SERVICE_FALLBACK_CHAIN[lang])
 * @returns {{value:string, isFallback:boolean, usedLang:(string|null)}}
 */
export function pickLangChain(jsonb, lang, chain) {
  const order =
    (Array.isArray(chain) && chain.length > 0 && chain) ||
    SERVICE_FALLBACK_CHAIN[lang] ||
    [lang, "en", "fr"];
  if (jsonb && typeof jsonb === "object" && !Array.isArray(jsonb)) {
    for (const key of order) {
      const v = jsonb[key];
      if (typeof v === "string" && v.trim() !== "") {
        return { value: v, isFallback: key !== lang, usedLang: key };
      }
    }
  }
  return { value: "", isFallback: false, usedLang: null };
}
